/**
 * The offline write queue.
 *
 * Mirrors `LocalData+OfflineQueue.swift`. A student ticking off a task on a bus
 * should see it tick, and it should still be ticked when they get signal. So the
 * write is applied locally, queued, and replayed on reconnect.
 *
 * Three rules the rest of the app depends on:
 *
 * - **Only idempotent writes are queued.** Creating a task carries a
 *   client-generated id, updating one is a whole-object PUT, and the timetable is
 *   a whole-document PUT. Replaying any of them twice is harmless. Anything that
 *   is not idempotent — submitting a test, spending AI budget — is never queued
 *   and fails honestly instead.
 * - **A 4xx drops the entry.** A queued write the server rejects will be rejected
 *   every time; retrying it forever means the queue never drains and the student
 *   is told "syncing" indefinitely. Only network failures and 5xx are retried.
 * - **Order is preserved.** Two edits to the same task must land in the order they
 *   were made, so the queue drains strictly in sequence, not in parallel.
 */

import { get, set } from 'idb-keyval'
import { ApiError } from '@modulus/api'
import { client } from './api'

const KEY = 'modulus.writeQueue.v1'

export interface QueuedWrite {
  id: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  body?: unknown
  /** What the UI calls this, for the offline banner and the failure toast. */
  label: string
  queuedAt: number
  attempts: number
}

type Listener = (queue: QueuedWrite[]) => void

let queue: QueuedWrite[] = []
let loaded = false
/** The in-flight drain, if there is one. Callers await it rather than being turned
 *  away — see the note on `drain`. */
let draining: Promise<void> | null = null
const listeners = new Set<Listener>()

async function persist(): Promise<void> {
  try {
    await set(KEY, queue)
  } catch {
    /* the in-memory queue still drains this session */
  }
}

function emit(): void {
  listeners.forEach((listener) => listener([...queue]))
}

export async function loadQueue(): Promise<void> {
  if (loaded) return
  loaded = true
  try {
    queue = (await get<QueuedWrite[]>(KEY)) ?? []
  } catch {
    queue = []
  }
  emit()
}

export function subscribeToQueue(listener: Listener): () => void {
  listeners.add(listener)
  listener([...queue])
  return () => listeners.delete(listener)
}

export function queueLength(): number {
  return queue.length
}

/**
 * Sends now, or queues for later if the network is the reason it failed.
 *
 * Returns the response when it went through, and `null` when it was queued — so a
 * caller that needs the server's answer (an id, a computed field) must not use
 * this. Those calls are not idempotent anyway.
 */
export async function writeThrough(
  entry: Omit<QueuedWrite, 'id' | 'queuedAt' | 'attempts'>,
): Promise<unknown | null> {
  await loadQueue()

  // Anything already queued has to go first, or a later edit could be overwritten
  // by an earlier one replaying after it.
  if (queue.length > 0) {
    await enqueue(entry)
    void drain()
    return null
  }

  try {
    return await send(entry)
  } catch (error) {
    if (isRetryable(error)) {
      await enqueue(entry)
      return null
    }
    throw error
  }
}

async function enqueue(entry: Omit<QueuedWrite, 'id' | 'queuedAt' | 'attempts'>): Promise<void> {
  queue.push({
    ...entry,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: Date.now(),
    attempts: 0,
  })
  await persist()
  emit()
}

function send(entry: Pick<QueuedWrite, 'method' | 'path' | 'body'>): Promise<unknown> {
  return client.request(entry.path, { method: entry.method, body: entry.body })
}

/** Network failures and 5xx. A 4xx is the server saying no, and it will keep
 *  saying no. */
function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500
  // A thrown TypeError from `fetch` is the browser saying it could not reach the
  // network at all.
  return error instanceof TypeError
}

/**
 * Drains strictly in order, stopping at the first entry that still cannot go.
 *
 * Concurrent calls **chain**: each one gets its own pass, queued behind whatever
 * is already running. Sharing a single in-flight promise instead looks equivalent
 * and is not — a drain that has already stopped on a failing entry would satisfy
 * the caller without retrying anything, and asking to drain means the situation
 * changed. The `online` event and a visibility change routinely fire within
 * milliseconds of each other, and that is exactly the case that has to work.
 */
export function drain(): Promise<void> {
  const pass = (draining ?? Promise.resolve()).then(runDrain)
  draining = pass.finally(() => {
    // Only the last pass clears the chain, so a later call still queues behind it.
    if (draining === pass) draining = null
  })
  return draining
}

async function runDrain(): Promise<void> {
  await loadQueue()
  if (queue.length === 0) return

  try {
    while (queue.length > 0) {
      const entry = queue[0]!
      try {
        await send(entry)
        queue.shift()
      } catch (error) {
        if (isRetryable(error)) {
          entry.attempts += 1
          // Give up after enough tries that this is clearly not a blip. Keeping it
          // forever means the banner never clears and the student is misled.
          if (entry.attempts < 8) break
        }
        // Rejected, or retried past patience. Drop it — silently keeping a write
        // that will never land is worse than losing one edit.
        queue.shift()
      }
      await persist()
      emit()
    }
  } finally {
    await persist()
    emit()
  }
}

/** Wired up once at startup. */
export function startQueueDraining(): () => void {
  const onOnline = () => void drain()
  window.addEventListener('online', onOnline)
  // Coming back to a backgrounded tab is the other moment connectivity is likely
  // to have returned without an `online` event firing.
  const onVisible = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) void drain()
  }
  document.addEventListener('visibilitychange', onVisible)

  void drain()

  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
